import React, { FunctionComponent, useRef, useState } from "react";
import styles from "./TextNewsletter.module.scss";
import TextNewsletterContent from "./TextNewsletterContent.model";

import { useInView } from "react-intersection-observer";
import Anchor from "components/Anchor/Anchor";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import formatDynamicTextColor from "blocks/helpers/formatDynamicTextColor";
import getProcessedRichtext from "blocks/helpers/getProcessedRichtext";
import getWrapperStyles from "blocks/helpers/getWrapperStyles";
import isBrowserContext from "helpers/isBrowserContext";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import BlockButton from "components/BlockButton/BlockButton";

const TextNewsletter: FunctionComponent<TextNewsletterContent> = (props) => {
    const processedHeadline = getProcessedRichtext(formatDynamicTextColor(props.content.headline));
    const processedBodyText = getProcessedRichtext(props.content.bodyText);
    const wrapperStyles = getWrapperStyles(props.settings);

    const mediaPositionClass = props.content.mediaPosition === "Media Right" ? ` ${styles.has_media_right}` : undefined;

    const { ref, inView } = useInView({ triggerOnce: true });
    const isBrowserClass = isBrowserContext() ? styles.isBrowser : undefined;
    const inViewClass = inView ? styles.isInView : undefined;

    const { executeRecaptcha } = useGoogleReCaptcha();
    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!executeRecaptcha) return;

        const token = await executeRecaptcha("newsletter");

        const form = e.currentTarget;
        const input = form.querySelector<HTMLInputElement>('input[name="g-recaptcha-response"]');
        if (input) input.value = token;

        form.submit();
    };

    return (
        <div className={combineClasses([styles.wrapper, ...wrapperStyles])}>
            <div className={combineClasses([styles.inner, mediaPositionClass])}>
                <div className={styles.embed}>
                    <form
                        onSubmit={handleFormSubmit}
                        className={styles.form}
                        method="post"
                        action="https://client3.mailmailmail.net/form.php?form=4658"
                    >
                        <table className={styles.table}>
                            <tbody>
                                <tr>
                                    <td>
                                        <input
                                            type="text"
                                            autoComplete="given-name"
                                            name="CustomFields[2]"
                                            placeholder="Fornavn"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            autoComplete="organization"
                                            name="CustomFields[15527]"
                                            placeholder="Firma"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <input
                                            type="email"
                                            name="email"
                                            autoComplete="email"
                                            required
                                            placeholder="Din e-mail"
                                        />
                                    </td>
                                    <td>
                                        <div>
                                            <select
                                                name="CustomFields[93072]"
                                                autoComplete="off"
                                                required
                                                defaultValue=""
                                            >
                                                <option
                                                    value=""
                                                    disabled
                                                    hidden
                                                >
                                                    Vælg din branche
                                                </option>
                                                <option value="Oplevelsessteder (campingplads, forlystelsespark, feriecentre mv.)">
                                                    Oplevelsessteder (campingplads, forlystelsespark, feriecentre mv.)
                                                </option>
                                                <option value="Kantine">Kantine</option>
                                                <option value="Café">Café</option>
                                                <option value="Restaurant">Restaurant</option>
                                                <option value="Hotel / Konferencested">Hotel / Konferencested</option>
                                                <option value="Convenience (to go)">Convenience (to go)</option>
                                                <option value="Grossist">Grossist</option>
                                                <option value="Supermarked">Supermarked</option>
                                            </select>
                                        </div>
                                    </td>
                                </tr>
                                <tr style={{ display: "none" }}>
                                    <td>First Name:</td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="first_name_mp"
                                            id="first_name_mp"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <button
                                            className={styles.button}
                                            type="submit"
                                        >
                                            <span className={styles.button_label}>Tilmeld</span>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <input
                            type="hidden"
                            name="g-recaptcha-response"
                        />
                    </form>
                </div>

                <div
                    className={combineClasses([styles.content, isBrowserClass, inViewClass])}
                    ref={ref}
                >
                    {/* Tagline */}
                    {props.content.tagline ? <span className={styles.tagline}>{props.content.tagline}</span> : null}

                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading
                            className={styles.headline}
                            headlineSize={props.content.headlineSize}
                            headlineWeight={props.content.headlineWeight}
                            innerHtml={processedHeadline}
                        ></DynamicHeading>
                    ) : null}

                    {/* Introductory */}
                    {props.content.introductory ? <p className={styles.introductory}>{props.content.introductory}</p> : null}

                    {/* Body Text */}
                    {processedBodyText ? (
                        <div
                            className={styles.bodyText}
                            dangerouslySetInnerHTML={processedBodyText}
                        ></div>
                    ) : null}

                    {/* Buttons */}
                    {props.content.buttons && props.content.buttons?.length > 0 && (
                        <div className={styles.buttons}>
                            {props.content.buttons.map((button) => {
                                return (
                                    <BlockButton
                                        key={button.contentUdi}
                                        label={button?.label}
                                        link={button?.link}
                                        target={button?.target}
                                        borderColor={button?.borderColor}
                                        backgroundColor={button?.backgroundColor}
                                        textColor={button?.textColor}
                                        backgroundHoverColor={button?.backgroundHoverColor}
                                        hoverTextColor={button?.hoverTextColor}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TextNewsletter;
